// 3rd party
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';
import { Subscription } from 'rxjs/Subscription';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import 'moment/min/locales';

// components
import { AppComponent } from 'app/app.component';

// services
import { AuthService } from 'app/shared/services/auth/auth.service';

// settings
import { environment } from 'environments/environment';

@Component({
  selector: 'app-login-feed',
  templateUrl: './login-feed.component.html',
  styleUrls: ['./login-feed.component.scss']
})
export class LoginFeedComponent implements OnInit, OnDestroy {

  form: FormGroup;

  private themeWrapper = document.querySelector('body');


  isLoading = false;
  isLoadingFeed = false;
  hasFeedError = false;
  isMobile = false;
  loginHint: string;
  url: string;
  passHint: string = null;
  userHint: string = null;

  subscribe1: Subscription; // media change
  subscribe2: Subscription; // blog feed
  subscribe3: Subscription; // authservice login

  webviewCustomLogin = false;
  hasLogo = false;
  hasColors = false;
  isPng = false;

  logo: string = '';
  domain: string = '';
  currentYear = new Date().getFullYear();

  posts: any[] = [];

  constructor(
    private app: AppComponent,
    private authService: AuthService,
    private router: Router,
    public snackBar: MatSnackBar,
    private translate: TranslateService,
    public media: ObservableMedia,
    private http: HttpClient,
    private route: ActivatedRoute,
  ) {
    moment.locale('pt-br');

    this.form = new FormGroup({
      login: new FormControl(null, Validators.compose([Validators.required, Validators.maxLength(100)])),
      password: new FormControl(null, Validators.compose([Validators.required, Validators.maxLength(50)])),
      stayConnected: new FormControl(false)
    });
  }

  ngOnInit() {

    this.setCustomTheme();

    this.subscribe1 = this.media.subscribe((change: MediaChange) => {
      this.isMobile = (change.mqAlias === 'xs');
    });

    let userLogin = localStorage.getItem('lastUser');
    this.form.controls.login.setValue(userLogin);
    if (!this.isMobile) {
      this.getBlogFeed();
    }
  }

  ngOnDestroy() {
    if (this.subscribe1) this.subscribe1.unsubscribe();
    if (this.subscribe2) this.subscribe2.unsubscribe();
    if (this.subscribe3) this.subscribe3.unsubscribe();
  }

  login() {
    if (this.form.invalid)
      return;

    const login = this.form.value.login;
    const password = this.form.value.password;
    const stayConnected = this.form.value.stayConnected;

    this.setCustomTheme(login);

    this.validateUser(login);
    this.validatePass(password);

    if (this.passHint || this.userHint)
      return;

    this.isLoading = true;
    this.authService.login(login, password, stayConnected).then(cc => {
      this.isLoading = false;
      var redirectTo = this.getRedirectTo(cc.Domain);
      if (this.app.showApp) {
        this.router.navigate([redirectTo]);
      } else {
        this.translate.get('Translate.ErrorOcurred').subscribe(val => this.openSnackbar(val));
      }
    }, err => {
      let body = err._body;
      this.isLoading = false;

      if (body) {
        if (body.includes('Field "password" is incorrect.')) {
          this.translate.get('Translate.WrongPassword').subscribe(val => this.openSnackbar(val));
          return;
        }
        if (body.includes('Field "login" is incorrect.')) {
          this.translate.get('Translate.InvalidUser').subscribe(val => this.openSnackbar(val));
          return;
        }

        if (body.includes('Could not login')) {
          this.translate.get('Translate.InvalidDomain').subscribe(val => this.openSnackbar(val));
          return;
        }
      } else {
        this.translate.get('Translate.ErrorOcurred').subscribe(val => this.openSnackbar(val));
      }
    });
  }

  openSnackbar(message: string) {
    this.snackBar.open(message, null, { duration: 3000 });
  }

  validatePass(password: string) {
    this.passHint = null;
    if (!password)
      this.translate.get('Translate.FieldCannotBeNull').subscribe((val) => this.passHint = val);
  }

  validateUser(userLogin: string) {
    this.userHint = null;
    if (!userLogin) {
      this.translate.get('Translate.FieldCannotBeNull').subscribe((val) => this.userHint = val);
      return;
    }

    let login = userLogin.split('@');
    if (!login || login.length < 2 || login[login.length - 1].length < 2) {
      this.translate.get('Translate.EmptyDomain').subscribe((val) => this.userHint = val);
      return;
    }
  }

  getBlogFeed() {
    this.isLoadingFeed = true;
    this.subscribe2 = this.http.get('https://login-api.qualiex.com/api/feeds?page=1').subscribe((data: any[]) => {
      this.isLoadingFeed = false;
      this.posts = data.map(d => {
        return {
          ...d,
          shareLinks: {
            facebook: `http://www.facebook.com/share.php?u=${d.url}`,
            twitter: `http://twitter.com/share?text=${d.title.replace(/\#/g, '')}&url=${d.url}`, // O compartilhameno do twitter buga quando possui '#' no texto p/ compartilhar
            gplus: `https://plus.google.com/share?url=${d.url}`,
            linkedin: `http://www.linkedin.com/shareArticle?mini=true&url=${d.url}&title=${d.title}&summary=${d.title}`,
          }
        };
      });
    }, () => {
      this.isLoadingFeed = false;
      this.hasFeedError = true;
    });
  }

  setCustomTheme(login: string = '') {
    if(!localStorage.getItem('domainApp'))
      this.setDefaultTheme();
    
    if(localStorage.getItem('hasLoginLogo') && localStorage.getItem('logologin')){
      this.logo = localStorage.getItem('logologin');
      this.hasLogo = localStorage.getItem('hasLoginLogo') === 'true';
    }
    
    if(login != null && this.domain !== login.slice(login.indexOf('@') + 1)) {
      localStorage.setItem('domainApp', login.slice(login.indexOf('@') + 1));
      this.domain = localStorage.getItem('domainApp')
      this.setDefaultTheme();
    }

    this.themeWrapper.className = localStorage.getItem('themeName');
    if(!this.domain)
      return;

    this.authService.getPermissionCustomLogin(this.domain).subscribe(response => {
      
      let hasPermission = JSON.parse(response).HasPermission.toString();
      localStorage.setItem('webviewCustomLogin', hasPermission);

      if (JSON.parse(response).HasPermission) {
        this.authService.getTheme(this.domain).subscribe(result => {

          let hasColors = result != null;
          localStorage.setItem('hasColors', hasColors.toString());
          if (hasColors) {
            let colors = JSON.parse(result.substring(0, result.length - 1).substring(1));

            localStorage.setItem('themeName', colors.Name);
            this.themeWrapper.className = localStorage.getItem('themeName');

          }
          this.authService.getSiteLogo(this.domain).subscribe(data => {

            let hasLogo = data != null;
            localStorage.setItem('hasSiteLogo', hasLogo.toString());

            if (hasLogo) {
              localStorage.setItem('typelogosite', JSON.parse(data).LogoNameLogin.split('.')[1]);
              localStorage.setItem('logosite', JSON.parse(data).BytesConverted);
              this.logo = localStorage.getItem('logosite')
              this.isPng = localStorage.getItem('typelogologin') === 'png';
            }

            this.authService.getLoginLogo(this.domain).subscribe(data => {

              let hasLogo = data != null;
              localStorage.setItem('hasLoginLogo', hasLogo.toString());

              if (hasLogo) {
                localStorage.setItem('typelogologin', JSON.parse(data).LogoNameLogin.split('.')[1]);
                localStorage.setItem('logologin', JSON.parse(data).BytesConverted);
                this.hasLogo = hasLogo;
                this.logo = localStorage.getItem('logologin');
              }
            });
          });
        });
      }
    });
  }

  setDefaultTheme(){
    this.hasLogo = false;
    localStorage.setItem('themeName', 'Default');
    localStorage.setItem('hasSiteLogo', 'false');
    localStorage.setItem('hasLoginLogo', 'false');
    localStorage.setItem('webviewCustomLogin', 'false');
  }

  getRedirectTo(ccDomain: string): string {
    var redirectTo = `/${ccDomain}/instruments`;

      this.route.queryParamMap.subscribe(params => {
        const param = params.get('redirectTo');
        
        if(param)
          redirectTo = params.get('redirectTo');
      });

      return redirectTo;
  }
}



// WEBPACK FOOTER //
// ./src/app/login/login-feed/login-feed.component.ts